import axiosAPI from '../../utils/client/axiosAPI';
import { MediaResponse, ResponseWithPagination, Pagination } from './contracts';

/**
 * Represents a Media item within the application and handles API interactions.
 */
export class Media implements MediaResponse {
  public readonly id: number;
  public readonly name: string;
  public readonly path: string;
  public readonly url: string;
  public readonly size?: number;

  constructor(data: MediaResponse) {
    this.id = data.id;
    this.name = data.name;
    this.path = data.path;
    this.url = data.url;
    this.size = data.size ?? 0;
  }
  /**
   * Provides the full, absolute URL to the media file for the <img> tag.
   */
  public get fullUrl(): string {
    const assetUrl = process.env.REACT_APP_ASSET_URL;

    return `${assetUrl}${this.url}`;
  }

  // --- STATIC API METHODS ---

  /**
   * Fetches a paginated list of all media items from the library,
   * with optional search functionality.
   * @param page - The page number to fetch.
   * @param search - An optional search string to filter by name.
   */
  public static async all(
    page: number = 1,
    search: string = '',
    limit: number = 20
  ): Promise<{ data: Media[]; pagination: Pagination }> {
    // <-- Note the updated return type

    const params = new URLSearchParams({
      page: page.toString(),
      per_page: limit.toString()
    });

    if (search) {
      params.append('search', search);
    }

    // The API response itself has the shape ResponseWithPagination<MediaResponse>
    const { data: response } = await axiosAPI.get<
      ResponseWithPagination<MediaResponse>
    >(`admin/media?${params.toString()}`);

    // Manually construct the object to return
    return {
      data: response.data.map((mediaData) => new Media(mediaData)),
      pagination: response.meta // <-- Extract the 'meta' object for pagination
    };
  }

  /**
   * Uploads one or more files to the media library.
   * @param files - An array of File objects to upload.
   */
  public static async upload(files: File[]): Promise<Media[]> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('images[]', file);
    });

    const { data } = await axiosAPI.post<MediaResponse[]>(
      'admin/media',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return data.map((mediaData) => new Media(mediaData));
  }

  /**
   * Replaces the file for an existing media item.
   * @param mediaId - The ID of the media to replace.
   * @param file - The new image File object.
   */
  public static async replace(mediaId: number, file: File): Promise<Media> {
    const formData = new FormData();
    formData.append('image', file);

    const { data } = await axiosAPI.post<MediaResponse>(
      `/admin/media/${mediaId}/replace`,
      formData
    );

    return new Media(data);
  }

  /**
   * Updates an existing media item (name and/or file).
   * @param mediaId The ID of the media to update.
   * @param data The data to update: an optional name and/or an optional file.
   */
  public static async update(
    mediaId: number,
    data: { name?: string; file?: File }
  ): Promise<Media> {
    const formData = new FormData();

    if (data.name) {
      formData.append('name', data.name);
    }
    if (data.file) {
      formData.append('image', data.file);
    }

    // --- THIS IS THE FIX ---
    // Add the _method field to tell Laravel to treat this POST as a PUT
    formData.append('_method', 'PUT');

    // The request remains a POST, but Laravel will route it to the update method
    const { data: responseData } = await axiosAPI.post<MediaResponse>(
      `/admin/media/${mediaId}`,
      formData
    );

    return new Media(responseData);
  }

  /**
   * Deletes a media item from the library permanently.
   * @param mediaId - The ID of the media to delete.
   */
  public static async delete(mediaId: number): Promise<void> {
    await axiosAPI.delete(`admin/media/${mediaId}`);
  }
}
